import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, ButtonGroup } from "react-bootstrap";
import NavBar from "../Components/NavBar/NavBar";
import Footer from "../Components/Footer/Footer";

const CheckStatus = () => {
    const [trackingId, setTrackingId] = useState("");
    const [statusData, setStatusData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCheckStatus = async (e) => {
        e.preventDefault();
        setError(null);

        if (!trackingId.trim()) {
            setError("Please enter a valid tracking ID");
            return;
        }

        setIsLoading(true);

        try {
            await new Promise(resolve => setTimeout(resolve, 1500));

            const statusTypes = ["approved", "processing", "rejected"];
            const mockStatus = statusTypes[Math.floor(Math.random() * statusTypes.length)];
            const mockUpdate = new Date().toISOString();

            // const response = await fetch(`/api/status/${trackingId}`);
            // const data = await response.json();
            // console.log('API response:', data);

            console.log('Mock API response:', {
                status: mockStatus,
                lastUpdated: mockUpdate,
                trackingId: trackingId
            });

            setStatusData({
                status: mockStatus,
                lastUpdated: mockUpdate,
                estimatedCompletion: mockStatus === "processing"
                    ? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString()
                    : null
            });
        } catch (err) {
            setError("Failed to fetch status. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setTrackingId("");
        setStatusData(null);
        setError(null);
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case "approved": return "success";
            case "processing": return "warning";
            case "rejected": return "danger";
            default: return "secondary";
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "approved": return "#28a745";
            case "processing": return "#ffc107";
            case "rejected": return "#dc3545";
            default: return "#6c757d";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "approved": return <i className="bi bi-check-circle-fill me-2"></i>;
            case "processing": return <i className="bi bi-hourglass-split me-2"></i>;
            case "rejected": return <i className="bi bi-x-circle-fill me-2"></i>;
            default: return <i className="bi bi-question-circle-fill me-2"></i>;
        }
    };

    return (
        <div className="d-flex flex-column min-vh-100" style={{
            background: statusData
                ? `linear-gradient(135deg, #f8f9fa 0%, ${getStatusColor(statusData.status)} 100%)`
                : "linear-gradient(135deg, #f8f9fa 0%, rgb(51, 151, 252) 100%)",
            fontSize: "14px"
        }}>
            <Helmet>
                <title>Track Application Status | NexGen</title>
                <meta name="description" content="Check your application status in real-time" />
            </Helmet>

            <NavBar />

            <main className="flex-grow-1 py-5">
                <Container>
                    <Row className="justify-content-center text-center mb-3">
                        <Col xs={12} md={10} lg={8}>
                            <h1 className="fw-bold mb-2" style={{ fontSize: "clamp(16px, 5vw, 18px)" }}>
                                <i className="bi bi-clipboard2-pulse me-2"></i>
                                Application Tracker
                            </h1>
                            <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                                Enter your tracking ID to check current status
                            </p>
                        </Col>
                    </Row>

                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8} xl={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-3 p-md-4">
                                    <Form onSubmit={handleCheckStatus}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-medium" style={{ fontSize: "14px" }}>
                                                <i className="bi bi-upc-scan me-2"></i>
                                                Tracking Number
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={trackingId}
                                                onChange={(e) => setTrackingId(e.target.value)}
                                                placeholder="e.g. APP-2023-ABCDEF"
                                                style={{ fontSize: "14px" }}
                                            />
                                        </Form.Group>

                                        <ButtonGroup className="w-100 gap-2">
                                            <Button
                                                type="submit"
                                                variant={statusData ? getStatusVariant(statusData.status) : "primary"}
                                                className="fw-medium flex-grow-1"
                                                disabled={isLoading}
                                                style={{ fontSize: "14px", padding: "8px 16px" }}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            className="me-2"
                                                        />
                                                        Checking...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-search me-2"></i>
                                                        Check Status
                                                    </>
                                                )}
                                            </Button>

                                            {(trackingId || statusData) && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={handleReset}
                                                    style={{ fontSize: "14px", padding: "8px 16px" }}
                                                >
                                                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                                                    Reset
                                                </Button>
                                            )}
                                        </ButtonGroup>
                                    </Form>
                                </Card.Body>
                            </Card>

                            {error && (
                                <Alert variant="danger" className="mt-3" style={{ fontSize: "14px" }}>
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            {statusData && (
                                <Card className="mt-3 border-0 shadow-sm">
                                    <Card.Body className="p-3 p-md-4">
                                        <h5 className="mb-3 d-flex align-items-center" style={{ fontSize: "16px" }}>
                                            <i className="bi bi-card-checklist me-2"></i>
                                            Application Details
                                        </h5>

                                        <div className="d-flex flex-column gap-2">
                                            <div className="d-flex align-items-center flex-wrap">
                                                <span className="me-2 fw-medium" style={{ fontSize: "14px" }}>Status:</span>
                                                <Alert
                                                    variant={getStatusVariant(statusData.status)}
                                                    className="py-1 px-3 mb-0 d-inline-flex align-items-center rounded-pill"
                                                    style={{ fontSize: "14px" }}
                                                >
                                                    {getStatusIcon(statusData.status)}
                                                    {statusData.status.charAt(0).toUpperCase() + statusData.status.slice(1)}
                                                </Alert>
                                            </div>

                                            <div className="d-flex align-items-center text-muted" style={{ fontSize: "14px" }}>
                                                <i className="bi bi-clock-history me-2"></i>
                                                <span>Last updated: {new Date(statusData.lastUpdated).toLocaleString()}</span>
                                            </div>

                                            {statusData.estimatedCompletion && (
                                                <div className="d-flex align-items-center text-muted" style={{ fontSize: "14px" }}>
                                                    <i className="bi bi-calendar-check me-2"></i>
                                                    <span>Estimated completion: {new Date(statusData.estimatedCompletion).toLocaleDateString()}</span>
                                                </div>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </Container>
            </main>

            <Footer />
        </div>
    );
};

export default CheckStatus;